import { assert } from '@ftr/contracts/shared/Assertions'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { LocalTimeRange } from '@ftr/contracts/type/shared'
import { SttEdit } from '@ftr/regional-api-contracts'
import { LocalTime } from '@js-joda/core'
import { RealTimeSttSegmentMap } from '../../../types'
import { ValidationErrorResult } from './operation-result'

export const SessionBoundaryErrors = [
  'start-end-cross-session-boundary',
  'start-end-not-in-session',
  'start-not-in-session', // Could be replaced by cross boundary
  'end-not-in-session', // Could be replaced by cross boundary
] as const
export const SealedBoundaryErrors = [
  'start-end-cross-sealed-boundary',
  'start-end-in-inaccessible-sealed',
  'start-in-inaccessible-sealed', // Could be replaced by cross boundary
  'end-in-inaccessible-sealed', // Could be replaced by cross boundary
] as const
export const HearingBoundaryErrors = ['start-end-cross-hearing-boundary'] as const

type SessionBoundaryError = (typeof SessionBoundaryErrors)[number]
type SealedBoundaryError = (typeof SealedBoundaryErrors)[number]
type HearingBoundaryError = (typeof HearingBoundaryErrors)[number]
export type BoundaryError = SessionBoundaryError | SealedBoundaryError | HearingBoundaryError

export function checkEditForBoundaryErrors(
  edit: SttEdit,
  segmentMap: RealTimeSttSegmentMap,
): BoundaryError | undefined {
  switch (edit.type) {
    case 'deleteUtterance':
      return undefined
    case 'softCreateUtterance':
      return checkTimeRangeForBoundaryErrors(
        new LocalTimeRange(edit.startTime.toLocalTime(), edit.endTime.toLocalTime()),
        segmentMap,
      )
    case 'upsertUtterance': {
      const { fragments } = edit
      assert(fragments.length)
      return checkTimeRangeForBoundaryErrors(
        new LocalTimeRange(fragments[0].startTime.toLocalTime(), fragments[fragments.length - 1].endTime.toLocalTime()),
        segmentMap,
      )
    }
    default:
      assertUnreachable(edit)
  }
}

export function boundaryErrorToValidationResult(error: BoundaryError): ValidationErrorResult {
  switch (error) {
    case 'start-end-cross-session-boundary':
    case 'start-end-cross-sealed-boundary':
    case 'start-end-cross-hearing-boundary':
    case 'start-end-not-in-session':
    case 'start-end-in-inaccessible-sealed':
      return {
        type: 'validationError',
        errorCode: error,
        invalidFields: ['startTime', 'endTime'],
      }
    case 'start-not-in-session':
    case 'start-in-inaccessible-sealed':
      return {
        type: 'validationError',
        errorCode: error,
        invalidFields: ['startTime'],
      }
    case 'end-not-in-session':
    case 'end-in-inaccessible-sealed':
      return {
        type: 'validationError',
        errorCode: error,
        invalidFields: ['endTime'],
      }
    default: {
      assertUnreachable(error)
    }
  }
}

function checkTimeRangeForBoundaryErrors(
  timeRange: LocalTimeRange,
  segmentMap: RealTimeSttSegmentMap,
): BoundaryError | undefined {
  // Check for out of session
  const sessionTimeRanges = segmentMap.sessionMarkers.flatMap(marker => marker.sessionTimes)
  const entirelyInASession = sessionTimeRanges.some(sessionTimeRange => sessionTimeRange.encloses(timeRange))
  if (!entirelyInASession) {
    const startInASession = sessionTimeRanges.some(sessionTimeRange => sessionTimeRange.contains(timeRange.start))
    const endInASession = sessionTimeRanges.some(
      sessionTimeRange =>
        sessionTimeRange.contains(timeRange.end) ||
        // Contains is not end inclusive
        sessionTimeRange.end.equals(timeRange.end),
    )
    if (startInASession && endInASession) {
      return 'start-end-cross-session-boundary'
    }

    if (!startInASession && !endInASession) {
      return 'start-end-not-in-session'
    }

    return startInASession ? 'end-not-in-session' : 'start-not-in-session'
  }

  // Check inaccessible sealed
  const inaccessibleSealedTimeRanges = segmentMap.sealingMarkers
    .filter(sealed => !sealed.canAccessSealedContent)
    .flatMap(marker => marker.sealedTimeRange)
  const startInaccessibleSealedRange = inaccessibleSealedTimeRanges.some(sealedTimeRange =>
    sealedTimeRange.contains(timeRange.start),
  )
  const endInaccessibleSealedRange = inaccessibleSealedTimeRanges.some(sealedTimeRange =>
    sealedTimeRange.contains(timeRange.end),
  )
  if (startInaccessibleSealedRange && endInaccessibleSealedRange) {
    return 'start-end-in-inaccessible-sealed'
  }
  if (startInaccessibleSealedRange) {
    return 'start-in-inaccessible-sealed'
  }
  if (endInaccessibleSealedRange) {
    return 'end-in-inaccessible-sealed'
  }

  // General check for boundary crossing
  const sealedTimeRanges = segmentMap.sealingMarkers.flatMap(marker => marker.sealedTimeRange)
  if (crossesBoundaries(timeRange, sealedTimeRanges)) {
    return 'start-end-cross-sealed-boundary'
  }
  const hearingTimeRanges = segmentMap.hearings.map(hearing => new LocalTimeRange(hearing.startTime, hearing.endTime))
  if (crossesBoundaries(timeRange, hearingTimeRanges)) {
    return 'start-end-cross-hearing-boundary'
  }

  return undefined
}

function crossesBoundaries(timeRange: LocalTimeRange, boundaries: LocalTimeRange[]): boolean {
  return boundaries.some(boundary => boundary.overlaps(timeRange) && !boundary.encloses(timeRange))
}

export function getAllBoundaryPointsSorted(segmentMap: RealTimeSttSegmentMap): LocalTime[] {
  return [
    segmentMap.sessionMarkers.flatMap(marker => [marker.sessionTimes.start, marker.sessionTimes.end]),
    segmentMap.sealingMarkers.flatMap(marker => [marker.sealedTimeRange.start, marker.sealedTimeRange.end]),
    segmentMap.hearings.flatMap(hearing => [hearing.startTime, hearing.endTime]),
  ]
    .flat()
    .toSorted((a, b) => a.compareTo(b))
}

export function getPreviousBoundaryPoint(time: LocalTime, segmentMap: RealTimeSttSegmentMap): LocalTime | undefined {
  const allBoundaries = getAllBoundaryPointsSorted(segmentMap)
  const boundariesBeforeTime = allBoundaries.filter(boundary => boundary.isBefore(time))
  return boundariesBeforeTime.at(-1)
}

export function getNextBoundaryPoint(time: LocalTime, segmentMap: RealTimeSttSegmentMap): LocalTime | undefined {
  const allBoundaries = getAllBoundaryPointsSorted(segmentMap)

  const boundariesAfterTime = allBoundaries.filter(boundary => boundary.isAfter(time))
  return boundariesAfterTime.at(0)
}
